import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { TooltipProvider } from '@/components/new-ui/tooltip';
import { parseStringToBool } from '@/utils/parseStringToBool';

/** Existing key of the old sidebar. 'true' means collapsed. */
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';
const MOBILE_BREAKPOINT = 768;
const DEFAULT_OPEN_BREAKPOINT = 1024;

type ShellSidebarContextValue = {
  /** Desktop state, remembered between visits */
  open: boolean;
  /** Phone state, lives for the session only */
  openMobile: boolean;
  isMobile: boolean;
  /** What the sidebar shows right now: the narrow icon rail when true */
  collapsed: boolean;
  setOpen: (open: boolean) => void;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
};

const ShellSidebarContext = createContext<ShellSidebarContextValue | null>(null);

const getIsMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

const readInitialOpen = () => {
  try {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (saved !== null) return !parseStringToBool(saved);
  } catch {
    // Storage is not available, fall through to the default
  }
  return window.innerWidth >= DEFAULT_OPEN_BREAKPOINT;
};

const writeOpen = (open: boolean) => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, open ? 'false' : 'true');
  } catch {
    // Storage is not available, the choice just lasts for this session
  }
};

const isEditableTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

export const ShellSidebarProvider = ({ children }: { children: ReactNode }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [open, setOpenState] = useState(readInitialOpen);
  const [openMobile, setOpenMobile] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => setIsMobile(getIsMobile());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    writeOpen(next);
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setOpenMobile((prev) => !prev);
    } else {
      setOpen(!open);
    }
  }, [isMobile, open, setOpen]);

  // Cmd/Ctrl+B, but never while the user types, the shortcut is bold in rich text fields
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'b' || !(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey || isEditableTarget(event.target)) return;
      event.preventDefault();
      toggleSidebar();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleSidebar]);

  const value = useMemo<ShellSidebarContextValue>(
    () => ({
      open,
      openMobile,
      isMobile,
      collapsed: isMobile ? !openMobile : !open,
      setOpen,
      setOpenMobile,
      toggleSidebar,
    }),
    [open, openMobile, isMobile, setOpen, toggleSidebar]
  );

  return (
    <ShellSidebarContext.Provider value={value}>
      <TooltipProvider delayDuration={0}>{children}</TooltipProvider>
    </ShellSidebarContext.Provider>
  );
};

export const useShellSidebar = () => {
  const context = useContext(ShellSidebarContext);
  if (!context) {
    throw new Error('useShellSidebar must be used within a ShellSidebarProvider');
  }
  return context;
};
